import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { GoogleAuthButton } from './GoogleAuthButton';

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  onSwitchToSignup?: () => void;
}

export function LoginForm({
  onSuccess,
  onForgotPassword,
  onSwitchToSignup,
}: LoginFormProps) {
  const { signIn, signInWithMagicLink, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const [showMagicLink, setShowMagicLink] = useState(false);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!showMagicLink && !password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (showMagicLink) {
      const result = await signInWithMagicLink(email);
      if (result.success) {
        toast({
          title: 'Magic link sent!',
          description: result.error || 'Check your email for the login link.',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: result.error,
          variant: 'destructive',
        });
      }
    } else {
      const result = await signIn(email, password);
      if (result.success) {
        onSuccess?.();
      } else {
        toast({
          title: 'Login failed',
          description: result.error,
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input
          id="login-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email}
          disabled={loading}
          autoComplete="email"
        />
        {errors.email && (
          <p className="text-sm text-sapling-error-100">{errors.email}</p>
        )}
      </div>

      {/* Password (hidden for magic link) */}
      {!showMagicLink && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="login-password">Password</Label>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-sapling-primary-200 hover:text-sapling-primary-100 dark:text-sapling-primary-100"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              disabled={loading}
              autoComplete="current-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-sapling-error-100">{errors.password}</p>
          )}
        </div>
      )}

      {/* Submit button */}
      <Button type="submit" className="w-full" loading={loading}>
        {showMagicLink ? 'Send Magic Link' : 'Log In'}
      </Button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      {/* Social login options */}
      <div className="space-y-2">
        <GoogleAuthButton />
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setShowMagicLink(!showMagicLink)}
          disabled={loading}
        >
          <Mail className="mr-2 h-4 w-4" />
          {showMagicLink ? 'Use Password' : 'Magic Link'}
        </Button>
      </div>

      {/* Switch to signup */}
      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="text-sapling-primary-200 hover:text-sapling-primary-100 dark:text-sapling-primary-100 font-medium"
        >
          Sign up
        </button>
      </p>
    </form>
  );
}
